import { 
    useState, 
    useEffect, 
    useLayoutEffect,
    useReducer,
    useRef,
    useCallback,
    useContext,
    createContext,
    Fragment,
    forwardRef,
    StrictMode
} from 'react';

import { 
    render,
    createPortal,
    unmountComponentAtNode
} from 'react-dom';

//import apiFetch from 'api-fetch';

import { 
    motion,
    AnimatePresence 
} from 'framer-motion';

import { MOTION_PRESETS } from './motion-presets.jsx';

import Gallery from './Gallery.jsx';


export const modalContext = createContext()


export const CloseButton = motion( forwardRef( ( {
    ready,
    allowExit = true,
    setClose
}, ref ) => {
    const variants = {
        top: {
            open: { 
                y: [0, 3.75, 3.75],
                rotate: [0, 0, -45],
                transition: {
                    times: [0, 0.5, 1],
                    duration: 0.5
                }
            },
            close: { 
                y: [3.75, 3.75, 0],
                rotate: [-45, 0, 0],
                transition: {
                    times: [0, 0.5, 1],
                    duration: 0.5
                }
            }
        },
        bottom: {
            open: { 
                y: [0, -3.75, -3.75],
                rotate: [0, 0, 45],
                transition: {
                    times: [0, 0.5, 1],
                    duration: 0.5
                }
            },
            close: { 
                y: [-3.75, -3.75, 0],
                rotate: [45, 0, 0],
                transition: {
                    times: [0, 0.5, 1],
                    duration: 0.5
                }
            }
        }
    }


    return (
        <motion.span 
            ref={ ref }
            className="wp-block-cwc-gallery__button-close"
            onClick={ () => setClose() }
        >
            <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <motion.rect 
                    x="4" 
                    y="7.5" 
                    width="16" 
                    height="1.5"
                    animate={ ready ? "open" : "close" }
                    exit="close"
                    variants={ variants.top } 
                    initial="close"
                />

                <motion.rect 
                    x="4" 
                    y="15" 
                    width="16" 
                    height="1.5"
                    animate={ ready ? "open" : "close" }
                    exit="close"
                    variants={ variants.bottom } 
                    initial="close"
                />
            </svg>
        </motion.span>
    )
} ) )


/**
 * Display pop up block
 * @prop {node} node - Pop Up block node.
 */
function Modal( {
    children,
    content,
    modalBlock,
    innerBlocks,
    id,
    position,
    width = 'auto',
    overlayBackgroundColor,
    overlayBackgroundCustomColor,
    preset = 'linear',
    root,
    speed = 1,
    closeOnClick = false,
    ...otherProps
} ) {    
    const modalContentRef = useRef()
    const closeButtonRef = useRef()
    const contentInnerRef = useRef()

    const [open, setOpen] = useState( true )
    const [ready, setReady] = useState( false )

    const { 
        container: containerVariants,
        content: contentVariants
    } = MOTION_PRESETS[preset]


    function lockDocument() {
        const doc = document.documentElement
        const scroll = window.innerWidth - doc.offsetWidth
        if ( scroll > 0 ) {
            doc.style.paddingRight = scroll + 'px' // set padding to root document
        }
        doc.classList.add( 'has-modal-open' )
    }


    function unlockDocument() {
        const doc = document.documentElement
        doc.style.paddingRight = ''
        doc.classList.remove( 'has-modal-open' )
    }


    function restore() {
        unmountComponentAtNode( root )
        if ( innerBlocks ) {
            innerBlocks.forEach( innerBlock => modalBlock.append( innerBlock ) )
        }
        root.remove()
        //modalBlock.innerHTML = content
    }

    function setClose() {
        setOpen( false )
    }

    function insertInnerBlocks() {
        innerBlocks.forEach( innerBlock => contentInnerRef.current.append( innerBlock ) )
    }


    useEffect( () => {
        if ( innerBlocks && open ) {
            insertInnerBlocks()
        }
    }, [open] )


    useEffect( () => {
        lockDocument()

        return () => {
            unlockDocument()
        }
    }, [] )


    return ( createPortal( 
        <modalContext.Provider value={ {
            content: {
                ref: modalContentRef
            },
            closeButton: {
                ref: closeButtonRef,
                ready,
                setClose
            }
        } }>
            <AnimatePresence>
                { open &&
                    <motion.div 
                        key={ id }
                        className={ `wp-block-cwc-gallery__modal-container has-max-height has-custom-content-position is-position-${position.replace( ' ', '-' )}` }
                        variants={ containerVariants }
                        initial="close"
                        animate="open"
                        exit="close"
                    >
                        <div 
                            ref={ modalContentRef }
                            className={ `wp-block-cwc-gallery__modal-dialog is-dialog-position-${position}${overlayBackgroundColor || overlayBackgroundCustomColor ? ' has-background' : ''}${overlayBackgroundColor ? ` has-${overlayBackgroundColor}-background-color` : ''}` }
                            style={ {
                                backgroundColor: overlayBackgroundCustomColor
                            } }
                            onMouseDown={ e => {
                                if ( closeOnClick && ( !modalContentRef.current.contains( e.target ) || closeButtonRef.current?.contains( e.target ) ) ) {
                                    setOpen( false )
                                }
                            } }
                        >
                            <motion.div 
                                className="wp-block-cwc-gallery__modal-content"
                                style={ {
                                    width
                                } }
                                variants={ contentVariants }
                                initial="close"
                                animate="open"
                                exit="close"
                                onAnimationComplete={ definition => {
                                    if ( definition == 'open' ) {
                                        setReady( true )
                                    } else if ( definition == 'close' ) {
                                        restore()
                                    }
                                } }
                            >
                                <div 
                                    ref={ contentInnerRef }
                                    className="wp-block-cwc-gallery__modal-content-inner" 
                                    //dangerouslySetInnerHTML={ { __html: content } } 
                                >
                                    <Gallery 
                                        ids={ otherProps?.ids }
                                        hasTitle={ !!otherProps?.hasTitle }
                                        hasCaption={ !!otherProps?.hasCaption }
                                        startedId={ id }
                                    />
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                }
            </AnimatePresence>
        </modalContext.Provider>, document.body 
    ) )
}


function init(e) {
    const imageBlock = e.target.closest( '.wp-block-image[data-attributes*="cwcGallery"]' ) // get image block
    if ( imageBlock ) {
        const root = document.createElement( 'div' )
        const attributes = JSON.parse( imageBlock.dataset.attributes )

        render( 
            <StrictMode>
                <Modal 
                    ids={ [attributes.id] }
                    overlayBackgroundColor={ attributes.cwcGallery?.overlayBackgroundColor } 
                    overlayBackgroundCustomColor={ attributes.cwcGallery?.overlayBackgroundCustomColor } 
                    modalBlock={ imageBlock } 
                    root={ root }
                    position="center-center"
                    closeOnClick={ attributes.cwcGallery?.closeOnClick }
                />
            </StrictMode>, root 
        )
    }


    const galleryBlock = e.target.closest( '.wp-block-gallery[data-attributes*="cwcGallery"]' ) 
    if ( galleryBlock && !e.target.closest( '.wp-block-gallery[data-attributes*="cwcGallery"] a' ) ) {
        const root = document.createElement( 'div' )
        const attributes = JSON.parse( galleryBlock.dataset.attributes )
        let startedId = 0

        const targetImageBlock = e.target.closest( '.wp-block-gallery[data-attributes*="cwcGallery"] > .wp-block-image' )
        const targetImage = targetImageBlock.querySelector( 'img[class*="wp-image-"]' )

        if ( targetImage ) {
            [ , startedId ] = targetImage.className.match( /wp-image-(\d+)/ ) // get image ID from className 
        }
        

        render( 
            <StrictMode>
                <Modal 
                    {...attributes.cwcGallery}
                    id={ Number( startedId ) }
                    ids={ attributes?.ids ?? [] }
                    modalBlock={ galleryBlock } 
                    root={ root }
                    position="center-center"
                    hasTitle
                />
            </StrictMode>, root 
        )
    }
}


document.addEventListener( 'DOMContentLoaded', e => {
    e.target.body.addEventListener( 'click', init )
} )